/**
 * The facts about one tile of the predicted view: what it is, what stands on it and what draws
 * from it. The tile line shows them; the options of a tile are decided from them.
 */
import {
  blockOf,
  GROWS,
  inGroup,
  isLoose,
  RULES,
  type Block,
  type CrewState,
  type Kind,
} from '@bramvasterd/terrella-rules';

import { drawnFrom, machineInfo, type MachineInfo } from './machines';

export interface TileFacts {
  tile: number;
  kind: Kind;
  /** The tile's level: above 0 raised, below 0 a pit. */
  elev: number;
  /** In a forest or deep water. */
  group: boolean;
  /** Milliseconds until a sapling grows up, or null. */
  growsIn: number | null;
  /** The machine standing on it, or null. */
  machine: MachineInfo | null;
  /** How many extractors draw from it. */
  drawnBy: number;
  /** Rock a charge broke (rules 24), lying there to be gathered. */
  broken: boolean;
  /** The block gathering it by hand gives (it is loose: grass, soil or broken rock), or null. */
  gathers: Block | null;
}

export function tileFacts(view: CrewState, tile: number, now: number): TileFacts {
  const kind = view.kind[tile] as Kind;
  return {
    tile,
    kind,
    elev: view.elev[tile] ?? 0,
    group: inGroup(view, tile),
    growsIn: GROWS.has(kind) ? Math.max(0, (view.since[tile] ?? 0) + RULES.growMs - now) : null,
    machine: machineInfo(view, tile, now),
    drawnBy: drawnFrom(view, tile),
    broken: view.shattered[tile] === 1,
    gathers: isLoose(view, tile) ? blockOf(kind) : null,
  };
}
